// src/services/roomsService.ts
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import {
  Room,
  RoomStatus,
  CreateRoomPayload,
  CreateRoomResponse,
  UpdateRoomPayload,
  UpdateRoomResponse,
} from "../types/types";
import { findConflictingRoom } from "../utils/roomConflicts";

function toSafeDate(value: any): Date {
  if (!value) return new Date(0);
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
}

function mapRoom(docSnap: QueryDocumentSnapshot<DocumentData>): Room {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    channelName: data.channelName ?? "",
    name: data.name ?? "",
    createdBy: data.createdBy ?? "",
    createdAt: toSafeDate(data.createdAt),
    startAt: toSafeDate(data.startAt),
    openDoorAt: toSafeDate(data.openDoorAt),
    endAt: toSafeDate(data.endAt),
    alertBeforeEndAt: toSafeDate(data.alertBeforeEndAt),
    status: (data.status ?? "inactive") as RoomStatus,
    topics: Array.isArray(data.topics) ? data.topics : [],
    participants: Array.isArray(data.participants) ? data.participants : [],
    maxParticipants: data.maxParticipants ?? 3,
  };
}

/**
 * One-off GET of the "rooms" collection (no realtime).
 * Returns an array of Room objects.
 */
export async function getRooms(): Promise<Room[]> {
  const snap = await getDocs(collection(db, "rooms"));
  return snap.docs.map(mapRoom);
}

/**
 * One-off GET of a single room by id. Returns null when it does not exist.
 * Used to resolve the "View your conversation" email deep link (#105) into the
 * full Room object the Call screen needs.
 */
export async function getRoomById(roomId: string): Promise<Room | null> {
  const snap = await getDoc(doc(db, "rooms", roomId));
  if (!snap.exists()) return null;
  return mapRoom(snap as QueryDocumentSnapshot<DocumentData>);
}

/**
 * Rejects a start time that overlaps another conversation the user hosts or is
 * registered for (#141). Returns the user-facing error, or null when free.
 * Used for creating, rescheduling and joining a conversation.
 */
export async function getScheduleConflictError(
  userId: string,
  startAt: Date,
  excludeRoomId?: string,
): Promise<string | null> {
  const roomsCollection = collection(db, "rooms");
  const [hosted, registered] = await Promise.all([
    getDocs(query(roomsCollection, where("createdBy", "==", userId))),
    getDocs(
      query(roomsCollection, where("participants", "array-contains", userId)),
    ),
  ]);
  const conflict = findConflictingRoom(
    [...hosted.docs, ...registered.docs].map(mapRoom),
    startAt,
    excludeRoomId,
  );
  if (!conflict) return null;

  const when = conflict.startAt.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return `You already have a conversation at that time ("${conflict.name}", ${when}).`;
}

/**
 * Creates a new room by directly writing to Firestore
 */
export async function createRoom(
  payload: CreateRoomPayload,
): Promise<CreateRoomResponse> {
  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;

    if (!userId) {
      return {
        success: false,
        error: "User not authenticated",
      };
    }

    const { name, startAt, topics = [], customTopicText } = payload;

    // Преобразуем startAt в Date (принимаем ISO-строку)
    const startAtDate = new Date(startAt);
    // Вычитаем 5 минут от startAt для openDoorAt
    const openDoorAtDate = new Date(startAtDate.getTime() - 5 * 60 * 1000);
    // Добавляем 20 минут к startAt для endAt
    const endAtDate = new Date(startAtDate.getTime() + 20 * 60 * 1000);
    // Вычитаем 2 минуты от endAt для alertBeforeEndAt
    const alertBeforeEndAtDate = new Date(endAtDate.getTime() - 2 * 60 * 1000);

    const conflictError = await getScheduleConflictError(userId, startAtDate);
    if (conflictError) {
      return { success: false, error: conflictError };
    }

    const roomsCollection = collection(db, "rooms");
    const docRef = await addDoc(roomsCollection, {
      channelId: "", // Будет установлен после получения ID документа
      name: name.trim(),
      createdBy: userId,
      createdAt: serverTimestamp(),
      startAt: Timestamp.fromDate(startAtDate),
      openDoorAt: Timestamp.fromDate(openDoorAtDate),
      endAt: Timestamp.fromDate(endAtDate),
      alertBeforeEndAt: Timestamp.fromDate(alertBeforeEndAtDate),
      status: "scheduled",
      topics: customTopicText ? [...topics, customTopicText.trim()] : topics,
      participants: [],
      maxParticipants: payload.maxParticipants ?? 3,
    });

    const roomId = docRef.id;
    const channelId = roomId;

    // Обновляем channelId после получения ID документа
    await updateDoc(docRef, {
      channelId: channelId,
    });

    console.log("[createRoom] Success: Created room", roomId);

    return {
      success: true,
      roomId: roomId,
      channelId: channelId,
    };
  } catch (error) {
    console.error("[createRoom] Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Updates an existing room in Firestore
 */
export async function updateRoom(
  roomId: string,
  payload: UpdateRoomPayload,
): Promise<UpdateRoomResponse> {
  try {
    const roomRef = doc(db, "rooms", roomId);

    const updateData: Record<string, any> = {
      updatedAt: serverTimestamp(),
    };

    updateData.name = payload.name?.trim();

    if (typeof payload.maxParticipants === "number") {
      updateData.maxParticipants = payload.maxParticipants;
    }

    const startAtDate = new Date(payload.startAt ?? 0);

    if (startAtDate.getTime()) {
      // Check against the host's schedule, not the editor's — an admin may be
      // editing someone else's room.
      const roomSnap = await getDoc(roomRef);
      const hostId = roomSnap.data()?.createdBy;
      if (hostId) {
        const conflictError = await getScheduleConflictError(
          hostId,
          startAtDate,
          roomId,
        );
        if (conflictError) {
          return { success: false, error: conflictError };
        }
      }

      const openDoorAtDate = new Date(startAtDate.getTime() - 5 * 60 * 1000);
      const endAtDate = new Date(startAtDate.getTime() + 20 * 60 * 1000);
      const alertBeforeEndAtDate = new Date(
        endAtDate.getTime() - 2 * 60 * 1000,
      );

      updateData.startAt = Timestamp.fromDate(startAtDate);
      updateData.openDoorAt = Timestamp.fromDate(openDoorAtDate);
      updateData.endAt = Timestamp.fromDate(endAtDate);
      updateData.alertBeforeEndAt = Timestamp.fromDate(alertBeforeEndAtDate);
    }

    if (payload.topics) {
      const topics = payload.topics ?? [];
      updateData.topics = payload.customTopicText
        ? [...topics, payload.customTopicText.trim()]
        : topics;
    }

    await updateDoc(roomRef, updateData);

    return { success: true };
  } catch (error) {
    console.error("[updateRoom] Error:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Adds a participant to a room and logs the registered action
 */
export async function addParticipantToRoom(
  roomId: string,
  userId: string,
): Promise<boolean> {
  try {
    const roomRef = doc(db, "rooms", roomId);
    await updateDoc(roomRef, {
      participants: arrayUnion(String(userId)),
      updatedAt: serverTimestamp(),
    });

    // Log participant action
    const actionsRef = collection(db, "rooms", roomId, "participantsActions");
    await addDoc(actionsRef, {
      userId: userId,
      status: "registered",
      date: serverTimestamp(),
    });

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Removes a participant from a room
 */
export async function removeParticipantFromRoom(
  roomId: string,
  userId: string,
): Promise<boolean> {
  try {
    const roomRef = doc(db, "rooms", roomId);
    await updateDoc(roomRef, {
      participants: arrayRemove(String(userId)),
      updatedAt: serverTimestamp(),
    });
    console.log(
      "[removeParticipantFromRoom] Success: Removed user",
      userId,
      "from room",
      roomId,
    );
    return true;
  } catch (error) {
    console.error("[removeParticipantFromRoom] Error:", error);
    return false;
  }
}

/**
 * Deletes a room from Firestore
 */
export async function deleteRoom(roomId: string): Promise<boolean> {
  try {
    const roomRef = doc(db, "rooms", roomId);
    await deleteDoc(roomRef);
    return true;
  } catch (error) {
    console.error("[deleteRoom] Error:", error);
    return false;
  }
}
